import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Loader2,
    RotateCw,
    X,
    ZoomIn,
    ZoomOut,
} from 'lucide-react';

import { useApi } from '../lib/api.js';
import { Button } from '../components/ui/Button.jsx';
import { Input, Select } from '../components/ui/Form.jsx';

// =============================================================================
// PendingUploadsPanel — chauffeur document review queue
// =============================================================================
// Replaces the old PendingUploadsSection on the compliance page.
//
// Row actions: Review (opens the review screen) / Reject / Approve (quick).
//
// Review screen: document preview on the left (image or PDF, zoom + rotate),
// fields on the right (licence number + expiry where the doc type has them),
// "Approve & next" saves the fields to the chauffeur/vehicle profile, approves
// the document and moves straight to the next one in the queue.
// =============================================================================

// Doc types with no expiry date — no expiry field shown.
const NO_EXPIRY_TYPES = new Set([
    'profile_picture',
    'v5',
    'photo_front_exterior',
    'photo_side_exterior',
    'photo_rear_exterior',
    'photo_front_interior',
    'photo_rear_interior',
]);

// Doc types where expiry is optional (everything else that shows the field
// requires it).
const OPTIONAL_EXPIRY_TYPES = new Set(['other', 'dbs_certificate', 'licence_paper']);

export function PendingUploadsPanel() {
    const api = useApi();
    const queryClient = useQueryClient();

    const pendingQuery = useQuery({
        queryKey: ['compliance', 'pending'],
        queryFn: () => api.get('/compliance/pending'),
        staleTime: 30 * 1000,
    });

    const pending = pendingQuery.data?.pending || [];
    const count = pending.length;

    const [rejectingDoc, setRejectingDoc] = useState(null);
    const [reviewingId, setReviewingId] = useState(null);
    const reviewingDoc = reviewingId ? pending.find((d) => d.id === reviewingId) || null : null;
    const reviewIndex = reviewingDoc ? pending.findIndex((d) => d.id === reviewingDoc.id) : -1;

    // After approving/rejecting the doc on screen, jump to the next one in the
    // queue (or the previous if it was the last). Closes when nothing's left.
    function advanceFrom(id) {
        const idx = pending.findIndex((d) => d.id === id);
        const next = pending[idx + 1] || pending[idx - 1] || null;
        setReviewingId(next && next.id !== id ? next.id : null);
    }

    function refreshAfterChange() {
        queryClient.invalidateQueries({ queryKey: ['compliance', 'pending'] });
        // Profile pages show licence numbers / expiries — refresh them too.
        queryClient.invalidateQueries({ queryKey: ['drivers'] });
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    }

    // Quick approve from the list row (no fields).
    const approveMutation = useMutation({
        mutationFn: (docId) => api.post(`/documents/${docId}/approve`),
        onSuccess: () => {
            toast.success('Document approved');
            refreshAfterChange();
        },
        onError: (err) => toast.error(err.message || 'Could not approve document'),
    });

    // Approve from the review screen — sends licence number / expiry.
    const reviewApproveMutation = useMutation({
        mutationFn: ({ docId, body }) => api.post(`/documents/${docId}/approve`, body),
        onSuccess: (_, variables) => {
            toast.success('Approved and saved to profile');
            advanceFrom(variables.docId);
            refreshAfterChange();
        },
        onError: (err) => toast.error(err.message || 'Could not approve document'),
    });

    const rejectMutation = useMutation({
        mutationFn: ({ docId, reason, notes }) =>
            api.post(`/documents/${docId}/reject`, { reason, notes }),
        onSuccess: (_, variables) => {
            toast.success('Document rejected');
            setRejectingDoc(null);
            if (variables.docId === reviewingId) advanceFrom(variables.docId);
            refreshAfterChange();
        },
        onError: (err) => toast.error(err.message || 'Could not reject document'),
    });

    return (
        <div className="card">
            <div className="card-header">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-sm font-semibold text-ink">
                            Pending uploads
                            {count > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-2xs font-semibold">
                                    {count}
                                </span>
                            )}
                        </h2>
                        <p className="text-xs text-ink-subtle mt-0.5">
                            Documents uploaded by chauffeurs awaiting your review.
                        </p>
                    </div>
                </div>
            </div>

            {pendingQuery.isLoading && (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-4">Loading…</p>
                </div>
            )}

            {pendingQuery.isError && (
                <div className="card-body">
                    <div className="flex items-start gap-3 text-sm text-status-danger">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>{pendingQuery.error?.message || 'Could not load pending uploads'}</div>
                    </div>
                </div>
            )}

            {pendingQuery.isSuccess && count === 0 && (
                <div className="card-body">
                    <p className="text-sm text-ink-subtle text-center py-4">
                        Nothing to review. New chauffeur uploads will appear here.
                    </p>
                </div>
            )}

            {pendingQuery.isSuccess && count > 0 && (
                <ul className="divide-y divide-paper-border-soft">
                    {pending.map((doc) => (
                        <PendingDocRow
                            key={doc.id}
                            doc={doc}
                            onReview={() => setReviewingId(doc.id)}
                            onApprove={() => approveMutation.mutate(doc.id)}
                            onReject={() => setRejectingDoc(doc)}
                            isWorking={
                                (approveMutation.isPending && approveMutation.variables === doc.id)
                                || (rejectMutation.isPending && rejectingDoc?.id === doc.id)
                            }
                        />
                    ))}
                </ul>
            )}

            {reviewingDoc && (
                <ReviewModal
                    key={reviewingDoc.id}
                    doc={reviewingDoc}
                    position={reviewIndex + 1}
                    total={count}
                    onClose={() => setReviewingId(null)}
                    onPrev={reviewIndex > 0 ? () => setReviewingId(pending[reviewIndex - 1].id) : null}
                    onNext={reviewIndex < count - 1 ? () => setReviewingId(pending[reviewIndex + 1].id) : null}
                    onApprove={(body) => reviewApproveMutation.mutate({ docId: reviewingDoc.id, body })}
                    onReject={() => setRejectingDoc(reviewingDoc)}
                    isApproving={reviewApproveMutation.isPending}
                />
            )}

            {/* Rendered after the review screen so it stacks on top of it */}
            {rejectingDoc && (
                <RejectModal
                    doc={rejectingDoc}
                    onCancel={() => setRejectingDoc(null)}
                    onConfirm={({ reason, notes }) =>
                        rejectMutation.mutate({ docId: rejectingDoc.id, reason, notes })
                    }
                    isSubmitting={rejectMutation.isPending}
                />
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// PendingDocRow — single row in the queue
// -----------------------------------------------------------------------------
function PendingDocRow({ doc, onReview, onApprove, onReject, isWorking }) {
    const docTypeLabel = formatDocTypeLabel(doc.doc_type, doc.custom_label);
    const expiresLabel = doc.expires_at ? formatExpiry(doc.expires_at) : null;
    const uploadedLabel = formatRelativeTime(doc.uploaded_at);

    return (
        <li className="px-6 py-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">{docTypeLabel}</div>
                    <div className="text-xs text-ink-subtle mt-0.5">
                        {doc.owner_type === 'driver' ? 'Chauffeur: ' : 'Vehicle: '}
                        <span className="text-ink-muted">{buildOwnerLabel(doc)}</span>
                    </div>
                    <div className="text-xs text-ink-subtle mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{doc.file_name}</span>
                        <span className="text-ink-faint">·</span>
                        <span>{(doc.file_size / 1024 / 1024).toFixed(1)} MB</span>
                        <span className="text-ink-faint">·</span>
                        <span>Uploaded {uploadedLabel}</span>
                        {expiresLabel && (
                            <>
                                <span className="text-ink-faint">·</span>
                                <span className={expiresLabel.expired ? 'text-status-danger font-medium' : ''}>
                                    {expiresLabel.text}
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                                        <Button variant="primary" onClick={onReview} disabled={isWorking}>
                        Review
                    </Button>
                </div>
            </div>
        </li>
    );
}

// -----------------------------------------------------------------------------
// ReviewModal — document preview + fields side by side
// -----------------------------------------------------------------------------
function ReviewModal({ doc, position, total, onClose, onPrev, onNext, onApprove, onReject, isApproving }) {
    const api = useApi();

    const [url, setUrl] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);

    const hasNumber = !!doc.number_label;
    const hasExpiry = !NO_EXPIRY_TYPES.has(doc.doc_type);
    const expiryRequired = hasExpiry && !OPTIONAL_EXPIRY_TYPES.has(doc.doc_type);

    const [number, setNumber] = useState(doc.current_number || '');
    const [expiry, setExpiry] = useState(doc.expires_at ? String(doc.expires_at).slice(0, 10) : '');

    const mime = (doc.mime_type || '').toLowerCase();
    const isHeic = /heic|heif/.test(mime) || /\.(heic|heif)$/i.test(doc.file_name || '');
    const isImage = mime.startsWith('image/') && !isHeic;
    const isPdf = mime === 'application/pdf' || /\.pdf$/i.test(doc.file_name || '');

    // Fetch a fresh signed URL for this document.
    useEffect(() => {
        let cancelled = false;
        api.get(`/documents/${doc.id}/download-url`)
            .then((r) => { if (!cancelled) setUrl(r.download_url); })
            .catch((e) => { if (!cancelled) setLoadError(e.message || 'Could not load document'); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc.id]);

    // Esc closes; arrow keys move between documents (not while typing).
    useEffect(() => {
        function onKey(e) {
            const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName);
            if (e.key === 'Escape') onClose();
            if (typing) return;
            if (e.key === 'ArrowRight' && onNext) onNext();
            if (e.key === 'ArrowLeft' && onPrev) onPrev();
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, onNext, onPrev]);

    function handleSubmit(e) {
        e.preventDefault();
        const trimmed = number.trim();
        if (hasNumber && !trimmed) {
            toast.error(`Enter the ${doc.number_label.toLowerCase()}`);
            return;
        }
        if (expiryRequired && !expiry) {
            toast.error('Enter the expiry date');
            return;
        }
        const body = {};
        if (hasNumber && trimmed) body.licence_number = trimmed;
        if (hasExpiry && expiry) body.expires_at = expiry;
        onApprove(body);
    }

    const profileHref = doc.owner_type === 'driver'
        ? `/chauffeurs/${doc.owner_id}`
        : `/vehicles/${doc.owner_id}`;

    const expiryInfo = expiry ? formatExpiry(expiry) : null;

    return (
        <div className="fixed inset-0 bg-ink/60 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-paper-card rounded-lg w-full max-w-6xl h-[92vh] shadow-xl overflow-hidden flex flex-col md:flex-row">

                {/* ---------------- Left: document preview ---------------- */}
                <div className="relative flex-1 min-h-[40vh] bg-slate-900 flex flex-col">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-950/60 text-slate-200 text-xs">
                        <span className="truncate">{doc.file_name}</span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            {isImage && (
                                <>
                                    <ToolbarButton title="Zoom out" onClick={() => setZoom((z) => Math.max(1, z - 0.5))} disabled={zoom <= 1}>
                                        <ZoomOut className="h-4 w-4" />
                                    </ToolbarButton>
                                    <ToolbarButton title="Zoom in" onClick={() => setZoom((z) => Math.min(4, z + 0.5))} disabled={zoom >= 4}>
                                        <ZoomIn className="h-4 w-4" />
                                    </ToolbarButton>
                                    <ToolbarButton title="Rotate" onClick={() => setRotation((r) => (r + 90) % 360)}>
                                        <RotateCw className="h-4 w-4" />
                                    </ToolbarButton>
                                </>
                            )}
                            {url && (
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open in new tab"
                                    className="p-1.5 rounded hover:bg-white/10"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto flex items-center justify-center p-3">
                        {loadError && (
                            <p className="text-sm text-red-300">{loadError}</p>
                        )}
                        {!loadError && !url && (
                            <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
                        )}
                        {!loadError && url && isImage && (
                            <img
                                src={url}
                                alt={doc.file_name}
                                className={zoom === 1 ? 'max-h-full max-w-full object-contain' : 'max-w-none'}
                                style={{
                                    transform: `rotate(${rotation}deg)`,
                                    width: zoom === 1 ? undefined : `${zoom * 100}%`,
                                    transition: 'transform 150ms',
                                }}
                            />
                        )}
                        {!loadError && url && !isImage && isPdf && (
                            <iframe src={url} title={doc.file_name} className="w-full h-full bg-white rounded" />
                        )}
                        {!loadError && url && !isImage && !isPdf && (
                            <div className="text-center text-slate-300 text-sm max-w-xs">
                                <p>
                                    {isHeic
                                        ? 'iPhone HEIC photos can’t be shown in the browser.'
                                        : 'This file type can’t be previewed here.'}
                                </p>
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 mt-3 px-3 py-2 rounded bg-white/10 hover:bg-white/20"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    Open the file
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {/* ---------------- Right: details + actions ---------------- */}
                <form
                    onSubmit={handleSubmit}
                    className="w-full md:w-[340px] flex-shrink-0 border-t md:border-t-0 md:border-l border-paper-border-soft flex flex-col"
                >
                    <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-paper-border-soft">
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={onPrev || undefined}
                                disabled={!onPrev}
                                className="p-1 rounded hover:bg-paper-hover disabled:opacity-30"
                                title="Previous (←)"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <span className="text-xs text-ink-subtle tabular-nums">
                                {position} of {total}
                            </span>
                            <button
                                type="button"
                                onClick={onNext || undefined}
                                disabled={!onNext}
                                className="p-1 rounded hover:bg-paper-hover disabled:opacity-30"
                                title="Next (→)"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1 rounded hover:bg-paper-hover"
                            title="Close (Esc)"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                        <div>
                            <h3 className="text-base font-semibold text-ink">
                                {formatDocTypeLabel(doc.doc_type, doc.custom_label)}
                            </h3>
                            <div className="text-sm text-ink-muted mt-1">
                                {doc.owner_type === 'driver' ? 'Chauffeur: ' : 'Vehicle: '}
                                {buildOwnerLabel(doc)}
                            </div>
                            <a
                                href={profileHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-ink-subtle hover:text-ink mt-1"
                            >
                                Open full profile
                                <ExternalLink className="h-3 w-3" />
                            </a>
                            <div className="text-xs text-ink-faint mt-2">
                                Uploaded {formatRelativeTime(doc.uploaded_at)}
                            </div>
                        </div>

                        {hasNumber && (
                            <div>
                                <Input
                                    label={doc.number_label}
                                    type="text"
                                    value={number}
                                    onChange={(e) => setNumber(e.target.value)}
                                    placeholder="As shown on the document"
                                    autoFocus
                                    maxLength={50}
                                    disabled={isApproving}
                                />
                                {doc.current_number && number.trim() !== doc.current_number && (
                                    <p className="text-xs text-amber-600 mt-1">
                                        Profile currently has {doc.current_number} — this will replace it.
                                    </p>
                                )}
                            </div>
                        )}

                        {hasExpiry && (
                            <div>
                                <Input
                                    label={expiryRequired ? 'Expiry date' : 'Expiry date (optional)'}
                                    type="date"
                                    value={expiry}
                                    onChange={(e) => setExpiry(e.target.value)}
                                    disabled={isApproving}
                                    autoFocus={!hasNumber}
                                />
                                {expiryInfo?.expired && (
                                    <p className="text-xs text-status-danger mt-1 font-medium">
                                        This date is in the past — the document is expired.
                                    </p>
                                )}
                            </div>
                        )}

                        {!hasNumber && !hasExpiry && (
                            <p className="text-sm text-ink-subtle">
                                Nothing to fill in for this document — check it and approve or reject.
                            </p>
                        )}
                    </div>

                    <div className="px-5 py-4 border-t border-paper-border-soft flex items-center justify-between gap-2">
                        <Button type="button" variant="secondary" onClick={onReject} disabled={isApproving}>
                            Reject
                        </Button>
                        <Button type="submit" variant="primary" onClick={handleSubmit} loading={isApproving} disabled={isApproving}>
                            {total > 1 ? 'Approve & next' : 'Approve'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ToolbarButton({ children, ...props }) {
    return (
        <button
            type="button"
            className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30"
            {...props}
        >
            {children}
        </button>
    );
}

// -----------------------------------------------------------------------------
// RejectModal — unchanged from the original compliance page
// -----------------------------------------------------------------------------
function RejectModal({ doc, onCancel, onConfirm, isSubmitting }) {
    const [reason, setReason] = useState('wrong_document');
    const [notes, setNotes] = useState('');
    const requiresNotes = reason === 'other';

    function handleConfirm() {
        if (requiresNotes && !notes.trim()) {
            toast.error('Please describe the reason');
            return;
        }
        onConfirm({ reason, notes: notes.trim() });
    }

    const docTypeLabel = formatDocTypeLabel(doc.doc_type, doc.custom_label);

    return (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4">
            <div className="bg-paper-card rounded-lg max-w-md w-full p-5 shadow-xl">
                <h3 className="text-sm font-semibold text-ink mb-1">Reject document</h3>
                <p className="text-xs text-ink-subtle mb-4">
                    {docTypeLabel} — {doc.file_name}
                </p>

                <div className="space-y-3">
                    <Select
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={isSubmitting}
                    >
                        <option value="wrong_document">Wrong document uploaded</option>
                        <option value="expired">Document has already expired</option>
                        <option value="unclear_image">Image is unclear or unreadable</option>
                        <option value="details_mismatch">Details don't match</option>
                        <option value="other">Other</option>
                    </Select>

                    {requiresNotes && (
                        <Input
                            label="Details"
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Brief explanation for the chauffeur"
                            disabled={isSubmitting}
                            maxLength={200}
                        />
                    )}

                    <p className="text-xs text-ink-subtle">
                        The chauffeur will see this reason in their portal and can upload a replacement.
                    </p>
                </div>

                <div className="flex gap-2 mt-5 justify-end">
                    <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirm} loading={isSubmitting}>
                        Confirm rejection
                    </Button>
                </div>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function buildOwnerLabel(doc) {
    if (doc.owner_type === 'driver') {
        const name = [doc.driver_first_name, doc.driver_last_name].filter(Boolean).join(' ');
        return name || 'Chauffeur';
    }
    const veh = [doc.vehicle_registration, [doc.vehicle_make, doc.vehicle_model].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(' · ');
    const partner = doc.vehicle_partner_first_name
        ? ` (paired with ${doc.vehicle_partner_first_name} ${doc.vehicle_partner_last_name || ''})`.trimEnd()
        : '';
    return (veh || 'Vehicle') + partner;
}

function formatDocTypeLabel(docType, customLabel) {
    if (docType === 'other' && customLabel) return customLabel;
    const map = {
        driving_licence: 'Driving licence',
        licence_paper:   'Licence counterpart',
        phv_badge:       'PHV badge',
        profile_picture: 'Profile photo',
        phd_licence:     'PHD licence',
        phd_badge:       'PHD badge',
        dbs_certificate: 'DBS certificate',
        operator_licence:'Operator licence',
        mot:             'MOT certificate',
        insurance:       'Insurance',
        v5:              'V5 logbook',
        phv_licence:     'PHV licence',
        photo_front_exterior: 'Front exterior photo',
        photo_side_exterior:  'Side exterior photo',
        photo_rear_exterior:  'Rear exterior photo',
        photo_front_interior: 'Front interior photo',
        photo_rear_interior:  'Rear interior photo',
        other:           'Other document',
    };
    return map[docType] || docType;
}

function formatExpiry(expiresAt) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(String(expiresAt).slice(0, 10) + 'T00:00:00');
    const formatted = expiry.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
    if (expiry < today) {
        return { text: `Expired ${formatted}`, expired: true };
    }
    return { text: `Expires ${formatted}`, expired: false };
}

function formatRelativeTime(timestamp) {
    if (!timestamp) return '';
    const ms = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
}
